import { jsPDF } from "jspdf";
import { PATHWAYS } from "../data/journeys.js";
import { PLACEMENTS, RETAKE_TEXT } from "../data/interpretations.js";
import { LOGO } from "../config/images.js";

const ROSE = [183, 110, 96];
const GOLD = [201, 162, 74];
const INK = [74, 42, 32];
const MUTED = [125, 90, 75];
const BLUSH = [251, 239, 234];
const IVORY = [255, 252, 246];

async function toDataUrl(url) {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    return await new Promise((ok) => {
      const r = new FileReader();
      r.onload = () => ok(r.result);
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

class Writer {
  constructor(doc) {
    this.doc = doc;
    this.W = doc.internal.pageSize.getWidth();
    this.H = doc.internal.pageSize.getHeight();
    this.M = 56;
    this.y = this.M;
  }
  page() {
    this.doc.addPage();
    this.doc.setFillColor(...IVORY);
    this.doc.rect(0, 0, this.W, this.H, "F");
    this.doc.setDrawColor(...GOLD);
    this.doc.setLineWidth(0.6);
    this.doc.rect(24, 24, this.W - 48, this.H - 48);
    this.y = this.M + 10;
  }
  ensure(h) {
    if (this.y + h > this.H - this.M - 10) this.page();
  }
  text(str, { size = 10.5, font = "helvetica", style = "normal", color = INK, gap = 6, indent = 0, lh = 1.45 } = {}) {
    const d = this.doc;
    d.setFont(font, style);
    d.setFontSize(size);
    d.setTextColor(...color);
    const lines = d.splitTextToSize(str, this.W - this.M * 2 - indent);
    const lineH = size * lh;
    lines.forEach((ln) => {
      this.ensure(lineH);
      d.text(ln, this.M + indent, this.y + size);
      this.y += lineH;
    });
    this.y += gap;
  }
  overline(str) {
    this.text(str.toUpperCase(), { size: 8.5, style: "bold", color: GOLD, gap: 4, lh: 1.2 });
  }
  heading(str, size = 22) {
    this.ensure(size * 2);
    this.text(str, { size, font: "times", style: "bold", color: ROSE, gap: 6, lh: 1.2 });
  }
  sub(str) {
    this.ensure(40);
    this.text(str, { size: 13, font: "times", style: "bold", color: INK, gap: 4, lh: 1.25 });
  }
  rule() {
    this.ensure(16);
    this.doc.setDrawColor(...GOLD);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.M, this.y + 4, this.M + 80, this.y + 4);
    this.y += 16;
  }
  bullet(str, n) {
    const d = this.doc;
    this.ensure(16);
    d.setFont("times", "bold");
    d.setFontSize(11);
    d.setTextColor(...ROSE);
    d.text(n !== undefined ? `${n}.` : "\u2022", this.M + 4, this.y + 10.5);
    this.text(str, { indent: 22, gap: 4 });
  }
}

function cover(w, profile, meta, logo) {
  const d = w.doc;
  d.setFillColor(...BLUSH);
  d.rect(0, 0, w.W, w.H, "F");
  d.setDrawColor(...GOLD);
  d.setLineWidth(1);
  d.rect(24, 24, w.W - 48, w.H - 48);
  d.setLineWidth(0.4);
  d.rect(30, 30, w.W - 60, w.H - 60);
  const c = w.W / 2;
  if (logo) {
    try {
      d.addImage(logo, "PNG", c - 135, 120, 270, 270, undefined, "FAST");
    } catch { /* logo optional */ }
  }
  d.setFont("helvetica", "bold");
  d.setFontSize(9);
  d.setTextColor(...GOLD);
  d.text("GODDESS UNIVERSITY", c, 432, { align: "center", charSpace: 2 });
  d.setFont("times", "bold");
  d.setFontSize(30);
  d.setTextColor(...ROSE);
  d.text("Your Goddess Journey", c, 468, { align: "center" });
  d.text("Roadmap", c, 500, { align: "center" });
  d.setFont("times", "italic");
  d.setFontSize(13);
  d.setTextColor(...MUTED);
  d.text("Your Sacred Path to Remembering, Healing, Expanding & Creating", c, 526, { align: "center" });
  d.setDrawColor(...GOLD);
  d.line(c - 50, 544, c + 50, 544);
  d.setFont("times", "bold");
  d.setFontSize(20);
  d.setTextColor(...INK);
  d.text(`Prepared for ${[profile.firstName, profile.lastName].filter(Boolean).join(" ")}`, c, 576, { align: "center" });
  d.setFont("helvetica", "normal");
  d.setFontSize(10);
  d.setTextColor(...MUTED);
  d.text(`${meta.date} at ${meta.time}  \u00B7  ${meta.timeZone}`, c, 600, { align: "center" });
  d.text(`Lunar phase: ${meta.lunar.name}`, c, 615, { align: "center" });
  d.setFont("times", "italic");
  d.setFontSize(11.5);
  d.setTextColor(...ROSE);
  d.splitTextToSize(meta.lunar.message, 380).forEach((ln, i) => d.text(ln, c, 642 + i * 15, { align: "center" }));
  d.setTextColor(...MUTED);
  d.setFont("times", "italic");
  d.setFontSize(11);
  d.text("Founded by Rev. Goddess LaMeel", c, w.H - 60, { align: "center" });
}

function journeySection(w, P, key) {
  const pl = P[key];
  w.page();
  w.overline(`${PLACEMENTS[key].label} \u2014 ${PLACEMENTS[key].tag}`);
  w.heading(pl.journey.name);
  w.text(pl.journey.purpose, { font: "times", style: "italic", size: 12, color: MUTED });
  w.rule();
  w.text(pl.explanation);
  if (pl.highlight) w.text(pl.highlight);
  (pl.flagNotes || []).forEach((n) => w.text(n, { color: MUTED }));
  if (pl.builds) { w.sub("How it builds upon your primary journey"); w.text(pl.builds); }
  if (pl.closeNote) w.text(pl.closeNote, { color: MUTED });
  if (pl.why && key === "primary") { w.sub("Why this is your starting place"); w.text(pl.why); }
  w.sub(key === "interwoven" ? "Supportive pathways" : key === "later" ? "Pathways to consider" : "Recommended pathway sequence");
  pl.sequence.forEach((pid, i) => {
    const p = PATHWAYS[pid];
    w.ensure(60);
    w.text(`${i + 1}. ${p.name}`, { font: "times", style: "bold", size: 12.5, color: ROSE, gap: 2 });
    w.text(p.short, { indent: 14, gap: 3 });
    if (key === "primary" || key === "interwoven") {
      p.practices.slice(0, key === "primary" ? 2 : 3).forEach((pr) => w.text(`\u2022 ${pr}`, { indent: 14, gap: 2, color: MUTED, size: 10 }));
    }
    w.y += 6;
  });
  if (key === "interwoven") { w.sub("Ways to weave it in"); pl.tips.forEach((t) => w.bullet(t)); }
  if (key === "later") { w.sub("Why later"); w.text(pl.why); }
  if (pl.optional?.length && key !== "later") {
    w.sub("Optional future exploration");
    w.text(pl.optional.map((p) => PATHWAYS[p].name).join("  \u00B7  "), { color: MUTED, size: 10 });
  }
}

export async function generateRoadmapPdf({ profile, meta, roadmap, output = "save" }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const w = new Writer(doc);
  const logo = await toDataUrl(LOGO.pdfCover);
  cover(w, profile, meta, logo);

  w.page();
  w.overline("Your Sacred Snapshot");
  w.heading(`Welcome, ${profile.firstName}`);
  w.text("This roadmap reflects where you are right now. It is a loving snapshot, not a fixed label, and your results may change as you evolve.");
  w.rule();
  w.sub("Assessment details");
  [["Date", meta.date], ["Time", meta.time], ["Time zone", meta.timeZone], ["Lunar phase", meta.lunar.name]].forEach(([k, v]) => w.text(`${k}: ${v}`, { gap: 2 }));
  w.y += 4;
  w.text(meta.lunar.message, { font: "times", style: "italic", size: 12, color: ROSE, gap: 8 });
  const cosmic = cosmicRows(profile);
  if (cosmic.length) {
    w.sub("Personal Cosmic Snapshot");
    cosmic.forEach(([k, v]) => w.text(`${k}: ${v}`, { gap: 2 }));
    w.text("These details are recorded for context and possible future use in a Personal Goddess Goldprint. They did not influence your journey results.", { color: MUTED, size: 9.5 });
  }
  w.y += 8;
  w.sub("Your four journeys at a glance");
  ["primary", "secondary", "interwoven", "later"].forEach((k) => w.text(`${PLACEMENTS[k].label} (${PLACEMENTS[k].tag}): ${roadmap.placements[k].journey.name}`, { gap: 3 }));

  ["primary", "secondary", "interwoven", "later"].forEach((k) => journeySection(w, roadmap.placements, k));

  w.page();
  w.overline("Your Practice");
  w.heading("Your First Three Goddess Steps");
  w.rule();
  roadmap.steps.forEach((s, i) => {
    w.text(`${i + 1}. ${s.label} \u2014 ${s.pathway}`, { font: "times", style: "bold", size: 12.5, color: ROSE, gap: 2 });
    w.text(s.action, { indent: 14, gap: 10 });
  });
  w.y += 10;
  w.heading("Return to Your Goddess Journey", 18);
  w.text(RETAKE_TEXT);
  w.text("Your answers were never stored. Keep this roadmap as your record, and return to Goddess University whenever you are ready to reassess.", { color: MUTED });

  const n = doc.getNumberOfPages();
  for (let i = 2; i <= n; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Goddess University  \u00B7  ${profile.firstName}'s Goddess Journey Roadmap  \u00B7  Page ${i} of ${n}`, w.W / 2, w.H - 36, { align: "center" });
  }
  const fname = `Goddess-Journey-Roadmap-${profile.firstName.replace(/[^a-z0-9]/gi, "") || "Goddess"}.pdf`;
  if (output === "base64") return { filename: fname, base64: doc.output("datauristring").split(",")[1] };
  doc.save(fname);
  return fname;
}

export function cosmicRows(profile) {
  const birthPlace = [profile.birthCity, profile.birthState, profile.birthCountry].filter(Boolean).join(", ");
  return [
    ["Date of birth", profile.dob && new Date(`${profile.dob}T12:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })],
    ["Time of birth", profile.birthTime],
    ["Birth place", birthPlace],
    ["Sun sign", profile.sunSign],
    ["Moon sign", profile.moonSign],
    ["Human Design type", profile.humanDesign],
  ].filter(([, v]) => v);
}
